import { FC, useEffect, useMemo, useState } from "react";
import styled from "@emotion/styled";
import { Text } from "@radix-ui/themes";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useInfiniteQuery } from "@tanstack/react-query";

import { atlasRoutes } from "@/app/router/routes";
import { LookupByUuidForm } from "@/components/forms/lookup-by-uuid-form";
import { api } from "@/modules/api";
import { CollectionSummarySchema } from "@/domains/openapi/types";
import { CollectionsSearchForm } from "@/pages/collections/root/collections-search-form";
import { CollectionsTable } from "@/pages/collections/root/collections-table";

const PAGE_SIZE = 50;

type CollectionsSearchParams = {
  query: string;
  spaceAccountId: string;
};

export const CollectionsRootPage: FC = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  const initialQuery = searchParams.get("query") ?? "";
  const initialSpaceAccountId = searchParams.get("spaceAccountId") ?? "";

  const [queryFilter, setQueryFilter] = useState<string>(initialQuery);
  const [spaceAccountFilter, setSpaceAccountFilter] = useState<string>(initialSpaceAccountId);

  useEffect(() => {
    const query = searchParams.get("query") ?? "";
    const spaceAccountId = searchParams.get("spaceAccountId") ?? "";
    setQueryFilter(query);
    setSpaceAccountFilter(spaceAccountId);
  }, [searchParams]);

  const trimmedQuery = queryFilter.trim();
  const trimmedSpaceAccount = spaceAccountFilter.trim();
  const hasFilters = trimmedQuery.length > 0 || trimmedSpaceAccount.length > 0;

  const { data, isLoading, isError, error, fetchNextPage, hasNextPage, isFetchingNextPage } =
    useInfiniteQuery({
      queryKey: ["collections-search", trimmedQuery, trimmedSpaceAccount],
      queryFn: ({ pageParam = 0 }) =>
        api.post("/internal/v1/collections/search", {
          body: {
            query: trimmedQuery || undefined,
            filter_by_space_account_id: trimmedSpaceAccount || undefined,
            limit: PAGE_SIZE,
            offset: pageParam,
          },
        }),
      enabled: hasFilters,
      initialPageParam: 0,
      getNextPageParam: (lastPage, allPages) => {
        const total = lastPage.data?.total ?? 0;
        const loaded = allPages.reduce((acc, page) => acc + (page.data?.results?.length ?? 0), 0);
        return loaded < total ? loaded : undefined;
      },
      staleTime: 5 * 60 * 1000,
      retry: 1,
    });

  const handleSearch = ({ query, spaceAccountId }: CollectionsSearchParams) => {
    const next = new URLSearchParams(searchParams);

    if (query) {
      next.set("query", query);
    } else {
      next.delete("query");
    }

    if (spaceAccountId) {
      next.set("spaceAccountId", spaceAccountId);
    } else {
      next.delete("spaceAccountId");
    }

    setSearchParams(next);
  };

  const handleViewCollection = (collectionId: string) => {
    navigate(atlasRoutes.collectionsView({ params: { collectionId } }).path);
  };

  const pages = data?.pages ?? [];
  const tableData = useMemo<CollectionSummarySchema[]>(
    () => pages.flatMap(page => page.data?.results ?? []),
    [pages]
  );
  const hasMoreResults = hasFilters && Boolean(hasNextPage);

  const handleLoadMore = () => {
    if (hasNextPage && !isFetchingNextPage) {
      fetchNextPage();
    }
  };

  const errorMessage =
    error && error instanceof Error
      ? error.message
      : isError
        ? "Error fetching collections."
        : null;

  return (
    <Container>
      <h1>Collections</h1>
      <DescriptionText>
        Quickly open a collection by UUID or search for collections by title, description, or owner.
      </DescriptionText>

      <Section>
        <SectionTitle>Lookup By UUID</SectionTitle>
        <LookupByUuidForm
          onSubmit={uuid =>
            navigate(atlasRoutes.collectionsView({ params: { collectionId: uuid } }).path)
          }
        />
      </Section>

      <Section>
        <SectionTitle>Search Collections</SectionTitle>
        <CollectionsSearchForm
          initialQuery={queryFilter}
          initialSpaceAccountId={spaceAccountFilter}
          onSearch={handleSearch}
        />
        {!hasFilters && <HintText>Enter a query or space account ID to search.</HintText>}
        {isLoading && hasFilters && <div>Loading...</div>}
        {errorMessage && <div>{errorMessage}</div>}

        {tableData.length > 0 && hasFilters && (
          <CollectionsTable
            data={tableData}
            pageSize={PAGE_SIZE}
            onViewCollection={handleViewCollection}
            onLoadMore={handleLoadMore}
            showLoadMore={hasMoreResults}
            loadMoreLabel={isFetchingNextPage ? "Loading..." : "Load More"}
            isLoadMoreDisabled={isFetchingNextPage}
          />
        )}
        {hasFilters && !isLoading && tableData.length === 0 && <div>No collections found.</div>}
      </Section>
    </Container>
  );
};

const Container = styled.div`
  width: 100%;
`;

const DescriptionText = styled(Text)`
  display: block;
  color: var(--gray-12);
  margin-bottom: 1rem;
`;

const Section = styled.section`
  margin-top: 32px;
`;

const SectionTitle = styled(Text)`
  display: block;
  font-weight: 600;
  margin-bottom: 12px;
`;

const HintText = styled(Text)`
  display: block;
  color: var(--gray-11);
  margin-bottom: 0.5rem;
`;
